import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField as MuiTextField,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { StudyFormInput } from '@domain/studyForm';
import { ImageGalleryInputs } from './ImageGalleryInputs';
import {
    StudyFormQuestionTypeValues,
    type StudyFormQuestion,
    type StudyFormQuestionType,
} from '@wwf/sdk';

type QuestionPath = `formDefinition.questions.${number}`;

export function getDefaultQuestion(
    type: StudyFormQuestionType
): StudyFormQuestion {
    const base = {
        id: '',
        title: '',
        type,
    } as const;
    if (type === 'YES_NO') {
        return { ...base, type: 'YES_NO', subQuestion: null };
    }
    if (type === 'TWO_CHOICES') {
        return { ...base, type: 'TWO_CHOICES', choice1: '', choice2: '' };
    }
    return { ...base, type: 'IMAGE_GALLERY', images: [] };
}

export function QuestionInputs({
    questionIndex,
    reservedQuestionIds = [],
    readOnly = false,
    onMoveUp,
    onMoveDown,
    canMoveUp,
    canMoveDown,
    onRemove,
}: {
    questionIndex: number;
    reservedQuestionIds?: readonly string[];
    readOnly?: boolean;
    onMoveUp(): void;
    onMoveDown(): void;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onRemove(): void;
}) {
    const notify = useNotify();
    const { control, getValues, setValue } = useFormContext<StudyFormInput>();
    const questionType = useWatch({
        control,
        name: `formDefinition.questions.${questionIndex}.type` as const,
    });
    const questionPath =
        `formDefinition.questions.${questionIndex}` as QuestionPath;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack gap={1.5}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="subtitle1">
                        Question {questionIndex + 1}
                    </Typography>
                    <Stack direction="row" gap={0.5}>
                        <IconButton
                            aria-label="Move question up"
                            onClick={onMoveUp}
                            disabled={readOnly || !canMoveUp}
                        >
                            <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            aria-label="Move question down"
                            onClick={onMoveDown}
                            disabled={readOnly || !canMoveDown}
                        >
                            <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            aria-label="Remove question"
                            color="error"
                            onClick={onRemove}
                            disabled={readOnly}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Stack>
                </Stack>
                <Controller
                    name={`${questionPath}.id`}
                    control={control}
                    rules={{
                        required: 'Question id is required',
                        validate: value =>
                            reservedQuestionIds.includes(value?.trim() ?? '')
                                ? 'This question id is reserved'
                                : true,
                    }}
                    render={({ field, fieldState }) => (
                        <MuiTextField
                            {...field}
                            label="Question id"
                            fullWidth
                            disabled={readOnly}
                            error={!!fieldState.error}
                            helperText={
                                fieldState.error?.message ??
                                'A unique ID through this form (required)'
                            }
                        />
                    )}
                />
                <Controller
                    name={`${questionPath}.title`}
                    control={control}
                    rules={{ required: 'Question title is required' }}
                    render={({ field, fieldState }) => (
                        <MuiTextField
                            {...field}
                            label="Question title"
                            fullWidth
                            disabled={readOnly}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    name={`${questionPath}.type`}
                    control={control}
                    render={({ field }) => (
                        <MuiTextField
                            {...field}
                            label="Type"
                            select
                            fullWidth
                            disabled={readOnly}
                            onChange={event => {
                                const nextType = event.target
                                    .value as StudyFormQuestionType;
                                const currentQuestion = getValues(questionPath);
                                if (
                                    !StudyFormQuestionTypeValues.includes(
                                        nextType
                                    )
                                ) {
                                    notify('Invalid question type', {
                                        type: 'error',
                                    });
                                    return;
                                }
                                const nextQuestion =
                                    getDefaultQuestion(nextType);
                                nextQuestion.id =
                                    currentQuestion?.id || nextQuestion.id;
                                nextQuestion.title =
                                    currentQuestion?.title || '';
                                setValue(questionPath, nextQuestion, {
                                    shouldDirty: true,
                                });
                            }}
                        >
                            {StudyFormQuestionTypeValues.map(type => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </MuiTextField>
                    )}
                />
                {questionType === 'YES_NO' ? (
                    <Controller
                        name={`${questionPath}.subQuestion`}
                        control={control}
                        render={({ field }) => (
                            <MuiTextField
                                {...field}
                                label="Sub-question"
                                fullWidth
                                disabled={readOnly}
                                value={field.value ?? ''}
                                onChange={event =>
                                    field.onChange(event.target.value || null)
                                }
                                helperText="Optional"
                            />
                        )}
                    />
                ) : null}
                {questionType === 'TWO_CHOICES' ? (
                    <>
                        <Controller
                            name={`${questionPath}.choice1`}
                            control={control}
                            rules={{ required: 'First choice is required' }}
                            render={({ field, fieldState }) => (
                                <MuiTextField
                                    {...field}
                                    label="Choice 1"
                                    fullWidth
                                    disabled={readOnly}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        <Controller
                            name={`${questionPath}.choice2`}
                            control={control}
                            rules={{ required: 'Second choice is required' }}
                            render={({ field, fieldState }) => (
                                <MuiTextField
                                    {...field}
                                    label="Choice 2"
                                    fullWidth
                                    disabled={readOnly}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </>
                ) : null}
                {questionType === 'IMAGE_GALLERY' ? (
                    <ImageGalleryInputs
                        questionIndex={questionIndex}
                        readOnly={readOnly}
                    />
                ) : null}
            </Stack>
        </Paper>
    );
}
